import {
  getPhaseLabels,
  type ControlPlaneSkillPlanProjection,
} from '@internal/spassu-code-contract';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import type { ControlPlaneSkillPlanView } from '../hooks/useControlPlaneSkillPlan';

const PHASE_LABELS = getPhaseLabels();

function skillRequestName(alias: string): string {
  return alias.replace(/^\/+/, '');
}

const useStyles = makeStyles(theme => ({
  section: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  item: {
    alignItems: 'flex-start',
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingLeft: 0,
    paddingRight: 0,
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

function SkillItems({
  plan,
  classes,
}: {
  plan: ControlPlaneSkillPlanProjection;
  classes: ReturnType<typeof useStyles>;
}) {
  if (plan.required.length === 0) return null;
  return (
    <List aria-label="Skills obrigatórias da fase">
      {plan.required.map((skill, index) => (
        <ListItem
          className={classes.item}
          key={`${skill.name}:${skill.version}:${skill.scopeType}`}
        >
          <ListItemText
            primary={`${index + 1}. ${skill.name}`}
            secondary={skill.description ?? 'Sem descrição informada'}
          />
          <Box className={classes.metadata}>
            {skill.canonicalAlias ? (
              <Chip
                size="small"
                label={`Nome para pedir: ${skillRequestName(
                  skill.canonicalAlias,
                )}`}
              />
            ) : null}
            <Chip size="small" label={`Versão ${skill.version}`} />
            <Chip
              size="small"
              label={
                skill.scopeType === 'corporate'
                  ? 'Obrigatória Spassu'
                  : 'Obrigatória do projeto'
              }
            />
            <Chip
              size="small"
              label={skill.origin === 'internal' ? 'Interna' : 'Comunidade'}
            />
          </Box>
        </ListItem>
      ))}
    </List>
  );
}

function PlanContent({
  plan,
  classes,
}: {
  plan: ControlPlaneSkillPlanProjection;
  classes: ReturnType<typeof useStyles>;
}) {
  const optionalSummary =
    plan.optionalCount > 0
      ? ` Há ${plan.optionalCount} opção(ões) configurada(s) para esta fase.`
      : '';
  return (
    <>
      <Typography variant="body2">
        Política vigente para a fase {PHASE_LABELS[plan.phase]}.
      </Typography>
      {plan.state === 'unavailable' ? (
        <Typography role="alert" color="error">
          Não foi possível consultar agora as skills obrigatórias. A sessão do
          agente continuará bloqueando a conclusão se não puder validar a
          política.
        </Typography>
      ) : null}
      {plan.state === 'not_required' ? (
        <Typography>Nenhuma skill obrigatória nesta fase.</Typography>
      ) : null}
      <SkillItems plan={plan} classes={classes} />
      {plan.state === 'ready' ? (
        <Typography>
          Ao iniciar a fase, o agente apresenta este conjunto obrigatório para
          confirmação uma única vez e carrega as versões vigentes sem pedir IDs.
        </Typography>
      ) : null}
      <Typography variant="body2">
        Você também pode pedir outra skill em linguagem natural, por exemplo
        “use a skill revisar arquitetura”, ou explicar a necessidade durante a
        fase. Não use comandos com “/” nem IDs. O agente pesquisa as opções e
        solicita confirmação antes de carregar; opcionais não substituem as
        obrigatórias.
        {optionalSummary}
      </Typography>
    </>
  );
}

export function ControlPlaneSkillPlanPanel({
  view,
}: {
  view: ControlPlaneSkillPlanView;
}) {
  const classes = useStyles();
  return (
    <Card variant="outlined">
      <CardContent className={classes.section}>
        <Typography variant="h6">Skills desta atividade</Typography>
        {view.loading ? (
          <CircularProgress aria-label="Carregando skills" />
        ) : null}
        {view.error ? (
          <Typography role="alert" color="error">
            {view.error}
          </Typography>
        ) : null}
        {view.error ? (
          <Button onClick={view.retry}>Tentar novamente</Button>
        ) : null}
        {view.plan ? <PlanContent plan={view.plan} classes={classes} /> : null}
      </CardContent>
    </Card>
  );
}
